import { IChatMessage } from '../../store/reducers/chatbotSlice';

// Cosmetic text-normalization rule tables. Order matters: each rule sees the output of the prior
// ones, matching the original chained `.replace()` calls exactly.
export const GENERIC_TEXT_RULES: ReplacementRule[] = [
    [/^Ach\b/, 'ACH'],
    [/\bam\b/gi, 'AM'],
    [/\bpm\b/gi, 'PM'],
    [/\bet\b/gi, 'ET'],
    [/\bach\b/gi, 'ACH'],
    [/\bautopay\b/gi, 'AutoPay'],
    [/\bpay\s+all\b/gi, 'Pay All'],
    [/\bplease\s+note\b/gi, 'Please Note'],
    [/\bcustomer\s+service\s+team\b/gi, 'Customer Service Team'],
    [/\bcustomer\s+portal\b/gi, 'Customer Portal'],
    [/\bsupport\s+request\b/gi, 'Support Request'],
    [/\bnew\s+request\b/gi, 'New Request'],
    [/\bsupport\b/gi, 'Support'],
    [/\bbusiness\s+hours\b/gi, 'Business hours'],
    [/\bmonday\b/gi, 'Monday'],
    [/\bfriday\b/gi, 'Friday'],
    [/\bi\b/g, 'I'],
    [/^note\s*:/i, 'Note:'],
];

export const PAYMENT_HISTORY_LINE_RULES: ReplacementRule[] = [
    [/\bpm\b/gi, 'PM'],
    [/\bam\b/gi, 'AM'],
    [/\bet\b/gi, 'ET'],
    [/\bpayment\s+history\b/gi, 'Payment History'],
    [/\bexport\b/gi, 'Export'],
    [/\bcontracts\s+page\b/gi, 'Contracts page'],
];

export const PLAIN_TEXT_RULES: ReplacementRule[] = [
    [/\bpm\b/gi, 'PM'],
    [/\bam\b/gi, 'AM'],
    [/\bet\b/gi, 'ET'],
    [/\bplease\s+note\b/gi, 'Please Note'],
    [/\bach\b/gi, 'ACH'],
    [/\bautopay\b/gi, 'AutoPay'],
    [/\bauto\s+ACH\s+payments\b/gi, 'Auto ACH Payments'],
    [/\bsigning\s+up\b/gi, 'Signing Up'],
    [
        /\bupdating\s+ACH\s+payment\s+information\s+currently\s+on\s+file\b/gi,
        'Updating ACH Payment Information Currently on File',
    ],
    [/\bcommercial\s+contracts\b/gi, 'Commercial Contracts'],
    [/\bcustomer\s+portal\b/gi, 'Customer Portal'],
    [/\bcontracts\b/gi, 'Contracts'],
    [/\bcontracts\b(?=\s+from\b)/gi, 'Contracts'],
    [/\bcontracts\s+page\b/gi, 'Contracts page'],
    [/\bgo\s+to\s+payments\b/gi, 'Go to Payments'],
    [/\bscheduled\s+payments\s+tab\b/gi, 'Scheduled Payments tab'],
    [/\bclick\s+cancel\b/gi, 'Click Cancel'],
    [/\bpayment\s+history\b/gi, 'Payment History'],
    [/\bexport\b/gi, 'Export'],
    [/\bdocuments\s+tab\b/gi, 'Documents tab'],
    [/\bnotification\s+tab\b/gi, 'Notification Tab'],
    [/\bemail\s+notification\b/gi, 'Email Notification'],
    [/\bsms\s+notification\b/gi, 'SMS notification'],
    [/\bmy\s+profile\b/gi, 'My profile'],
    [/\bmy\s+profile\s+tab\b/gi, 'My Profile tab'],
    [/\bgo\s+to\s+settings\b/gi, 'Go to Settings'],
    [/\bedit\s+profile\b/gi, 'Edit Profile'],
    [/\bclick\s+save\b/gi, 'Click Save'],
    [
        /\bcell\s+phone\s+number\s*\/\s*alternate\s+cell\s+phone\s+field\b/gi,
        'Phone Number / Alternate Phone Number field',
    ],
    [/\bsupport\b/gi, 'Support'],
    [/\bcustomer\s+support\b/gi, 'Customer Support'],
    [/\bcustomer service team\b/gi, 'Customer Service Team'],
    [/\bsupport request\b/gi, 'Support Request'],
    [/\bnew request\b/gi, 'New Request'],
    [/\bbusiness hours\b/gi, 'Business hours'],
    [/\bmonday\b/gi, 'Monday'],
    [/\bfriday\b/gi, 'Friday'],
    [/\bservice\s+hqtrs\b/gi, 'Service hqtrs'],
    [/\bi\b/g, 'I'],
    [/\bsubmit\s+a\s+support\s+request\b/gi, 'Submit a support request'],
];

export interface MessageBubbleProps {
    message: IChatMessage;
    onShowQuickActions?: () => void;
}

export type LooseRecord = Record<string, unknown>;

export interface GenericSection {
    title: string;
    summaryLines: string[];
    keyValues: Array<{ label: string; value: string }>;
    numberedItems: string[];
}

export interface ContractDetails {
    contractId: string;
    status: string;
    type: string;
    description: string;
    monthlyPayment: string;
    nextPaymentOn: string;
    principalAmount: string;
    lastPayment: string;
    progress: string;
    followUp: string | null;
}

export interface ContractSummary {
    contractId: string;
    type: string;
    description: string;
    monthlyPayment: string;
    nextPaymentOn: string;
    status: string;
    principalAmount: string;
    lastPayment: string;
    progress: string;
    additionalDetails?: Array<{ label: string; value: string }>;
}

export interface ContractFinancialItem {
    contractId: string;
    financedAmount: string;
    paidPrincipal: string;
    remainingAmount: string;
    status: string;
    description: string;
}

export interface ContractFinancialSummaryResponse {
    intro: string;
    items: ContractFinancialItem[];
    followUp: string | null;
}

export interface ContractMetricRow {
    contractId: string;
    metrics: Array<{ label: string; value: string }>;
}

export interface ContractMetricListResponse {
    intro: string;
    rows: ContractMetricRow[];
    followUp: string | null;
}

export interface ContractSnapshotItem {
    contractId: string;
    status: string;
    accountType: string;
    assetDesc: string;
    emiAmount: string;
    outstandingBalance: string;
    remainingPrincipal: string;
    pastDue: string;
    nextPaymentDate: string;
    nextPaymentAmount: string;
    autoPay: string;
    paymentsProgress: string;
    maturityDate: string;
    lastPaymentDate: string;
    lastPaymentAmount: string;
}

export interface ContractSnapshotResponse {
    intro: string;
    items: ContractSnapshotItem[];
}

export interface NextPaymentDueItem {
    contractId: string;
    nextPaymentDate: string;
    nextPaymentAmount: string;
    frequency: string;
}

export interface NextPaymentDueResponse {
    intro: string;
    items: NextPaymentDueItem[];
    followUp: string | null;
}

export interface ContractDateRangeItem {
    contractId: string;
    startDate: string;
    endDate: string;
}

export interface ContractDateRangeResponse {
    intro: string;
    items: ContractDateRangeItem[];
    followUp: string | null;
}

export interface ContractMetricBulletItem {
    contractId: string;
    value: string;
}

export interface ContractMetricBulletResponse {
    intro: string;
    metricLabel: string;
    items: ContractMetricBulletItem[];
    followUp: string | null;
}

export interface LinkedAssetItem {
    name: string;
    serialNumber: string;
    make: string;
    model: string;
    year: string;
    location: string;
}

export interface LinkedAssetResponse {
    intro: string;
    items: LinkedAssetItem[];
    followUp: string | null;
}

export interface SingleContractMetricResponse {
    contractId: string;
    metricLabel: string;
    value: string;
    followUp: string | null;
}

export interface SupportRequestItem {
    ticketNumber: string;
    status: string;
    subject: string;
    date: string;
    type: string;
    contract: string;
    description: string;
    hasStatus: boolean;
    hasContract: boolean;
}

export interface PaymentItem {
    contractId: string;
    paymentDate: string;
    referenceNumber: string;
    amount: string;
    method: string;
    dueDate: string;
    invoiceNumber: string;
    transactionType: string;
    invoiceMethod: string;
    status: string;
    appliedAmount: string;
    breakdowns?: PaymentBreakdownItem[];
}

export interface PaymentBreakdownItem {
    dueDate: string;
    invoiceNumber: string;
    transactionType: string;
    invoiceMethod: string;
    status: string;
    appliedAmount: string;
}

export interface AmortizationUpcomingPaymentItem {
    paymentDate: string;
    payment: string;
    principal: string;
    interest: string;
    remainingBalance: string;
    source: string;
}

export interface AmortizationScheduleResponse {
    intro: string;
    contractId: string;
    contractName: string;
    summaryLine: string;
    stats: Array<{ label: string; value: string }>;
    upcomingPayments: AmortizationUpcomingPaymentItem[];
    followUp: string | null;
}

export interface OpenInvoiceItem {
    contractId: string;
    dueDate: string;
    nextDueDate?: string;
    invoiceNumber: string;
    amountDue: string;
    remainingDue: string;
    pastDueAmount?: string;
    remainingPrincipalAmount?: string;
    autopayEnabled: string;
    action: string;
    overdue: boolean;
    // extended fields from pipe-separated API payload
    originalDueAmount: string;
    segregationDescription: string;
    segregationAmount: string;
    totalPendingScheduleAmount: string;
    invoiceDetailNumber: string;
    isActive: string;
    lateFee?: string;
}

export interface ScheduledPaymentItem {
    contractId: string;
    scheduledDate: string;
    amount: string;
    status: string;
    recordId: string;
    paymentOId: string;
    aspireContractId: string;
    createdTime: string;
}

export interface ScheduledPaymentsResponse {
    payments: ScheduledPaymentItem[];
    followUp: string | null;
    intro?: string | null;
}

/** An ordered [pattern, replacement] rule applied left-to-right over a string. */
export type ReplacementRule = [RegExp, string];
